import { ChangeEvent, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentCapturePage } from "@iddataweb/idw-common-ui/types";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { resources } from "@constants/hardcoded-strings";
import { DCUICaptureStepTypes } from "@enums/dcui-capture-step-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIStepResources } from "@idw-types/common/dcui/dcui-step-resources";
import { DCUIThemingStartPageCaptureStepValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { InputFormGroup } from "../../../../../../common/input-form-group/input-form-group";
import { RadioInputFormGroup } from "../../../../../../common/radio-input-form-group/radio-input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";
import { FileInputFormGroup } from "@containers/common/file-input-form-group/file-input-form-group";
import { ResourceCategoryTypes } from "@enums/resource-category-types";

type DCUIThemingStartPageCaptureStepContainerProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingStartPageCaptureStepValidationErrors;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    pageLevel: PageLevelTypes;
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    stepType: DCUICaptureStepTypes;
    stepResources: DCUIStepResources;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingStartPageCaptureStepContainer = (props: DCUIThemingStartPageCaptureStepContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        dcuiContent,
        inheritedDCUIContent,
        uploadFileToS3,
        pageLevel,
        layout,
        labelWidth,
        stepType,
        stepResources,
        updateDCUIContentPropertyValue
    } = props;

    const stepContentField = stepType as keyof DCUIContent;
    const stepContent = dcuiContent[stepContentField] as DCUIContentCapturePage;
    const defaultStepContent = inheritedDCUIContent[stepContentField] as DCUIContentCapturePage;
    const [startInstructionImageUploadInProgress, setStartInstructionImageUploadInProgress] = useState(false);

    const updateDCUIContentStepContentFieldValue = (key: keyof DCUIContentCapturePage, value: string | boolean) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            [stepContentField]: {
                ...stepContent,
                [key]: value
            }
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleStartInstructionsImageLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentStepContentFieldValue("label", event.target.value);
    };

    const handleResetStartInstructionsImageLabel = () => {
        updateDCUIContentStepContentFieldValue("label", defaultStepContent.label);
    };

    const handleUseCustomStartInstructionsImageChange = (newValue: string) => {
        updateDCUIContentStepContentFieldValue("useCustomStartInstructionImage", isStringTrue(newValue));
    };

    const handleResetUseCustomStartInstructionsImage = () => {
        updateDCUIContentStepContentFieldValue("useCustomStartInstructionImage", defaultStepContent.useCustomStartInstructionImage);
    };

    const handleStartInstructionsImageUrlChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
       
            setStartInstructionImageUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, `${stepType}-${ResourceCategoryTypes.DCUI_START_INSTRUCTION_IMAGES}`);
            if (uploadResult) {
                updateDCUIContentStepContentFieldValue("startInstructionsImageUrl", uploadResult);
            }
            setStartInstructionImageUploadInProgress(false);
        }

       
    };

    const handleResetStartInstructionsImageUrl = () => {
        updateDCUIContentStepContentFieldValue("startInstructionsImageUrl", defaultStepContent.startInstructionsImageUrl);
    };

    //todo - currently the label on the start page is the same as the title on the capture step page
    //need to find out if this is on purpose or not
    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={defaultStepContent.label}
                currentThemingPropertyValue={stepContent.label}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={`dcui-theming-step-${stepType}-start-instructions-image-label-input`}
                    automationId={`dcui-theming-step-${stepType}-start-instructions-image-label-input`}
                    disabled={isDisabled}
                    isRequired={true}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.startInstructionsImageLabel.label(stepResources.title)}
                    ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.startInstructionsImageLabel.label(stepResources.title)}
                    labelWidth={labelWidth}
                    layout={layout}
                    onChange={handleStartInstructionsImageLabelChange}
                    value={stepContent.label}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.startInstructionsImageLabel.tooltip(stepResources.title)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetStartInstructionsImageLabel}
                />
            </ThemingFormGroupWrapper>
            {validationErrors?.captureStepLabelError || null}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={defaultStepContent.useCustomStartInstructionImage}
                currentThemingPropertyValue={stepContent.useCustomStartInstructionImage}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`dcui-theming-step-${stepType}-use-custom-start-instructions-image-radio-input`}
                    automationId={`dcui-theming-step-${stepType}-use-custom-start-instructions-image-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.useCustomStartInstructionsImage.label(stepResources.title)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleUseCustomStartInstructionsImageChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={stepContent.useCustomStartInstructionImage ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.useCustomStartInstructionsImage.tooltip(stepResources.title)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetUseCustomStartInstructionsImage}
                />
            </ThemingFormGroupWrapper>
            {stepContent.useCustomStartInstructionImage && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={defaultStepContent.startInstructionsImageUrl}
                        currentThemingPropertyValue={stepContent.startInstructionsImageUrl}
                        pageLevel={pageLevel}
                    >
                        <FileInputFormGroup
                            controlId={`dcui-theming-step-${stepType}-custom-start-instructions-image-url-input`}
                            automationId={`$dcui-theming-step-${stepType}-custom-start-instructions-image-url-input`}
                            disabled={isDisabled}
                            isRequired={true}
                            label={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.startInstructionsImageUrl.label(stepResources.title)}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.startInstructionsImageUrl.label(stepResources.title)}
                            labelWidth={labelWidth}
                            layout={layout}
                            onChange={handleStartInstructionsImageUrlChange}
                            value={stepContent.startInstructionsImageUrl}
                            accept={"image/png, image/gif, image/jpeg, image/jpg"}
                            uploadInProgress={startInstructionImageUploadInProgress}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.startPage.sections.captureSteps.inputs.startInstructionsImageUrl.tooltip(stepResources.title)}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetStartInstructionsImageUrl}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors?.captureStepImageURLError || null}
                </>
            )}
        </div>
    );
};